import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Shield, ShieldAlert, Package, ListChecks, Plus, AlertTriangle, Trash2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const STRIDE = ["Spoofing", "Tampering", "Repudiation", "InfoDisclosure", "DoS", "ElevationOfPrivilege"];
const SAFETY = ["none", "low", "moderate", "high", "critical"];
const STATUSES = ["open", "mitigation_planned", "mitigated", "accepted"];
const CONTROL_STATUSES = ["not_started", "in_progress", "evidence_documented", "not_applicable"];

export default function Cybersecurity() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data);
      if (data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("Load projects:", e?.response?.status));
  }, [authLoading]);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><Shield className="w-3 h-3" /> Cybersecurity</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Threat Model · SBOM · NIST CSF 2.0
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Aligned with FDA Cybersecurity in Medical Devices (2023), IEC 81001-5-1, NIST CSF 2.0, and the EU Cyber
            Resilience Act. Advisory scaffolds only — a qualified cybersecurity reviewer must complete every artefact
            before submission.
          </p>
        </div>

        <div className="mb-6 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="cyber-project-select" className="mt-1">
              <SelectValue placeholder="Select project…" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {pid && (
          <Tabs defaultValue="threats">
            <TabsList data-testid="cyber-tabs">
              <TabsTrigger value="threats" data-testid="tab-threats"><ShieldAlert className="w-4 h-4 mr-1" /> Threat model</TabsTrigger>
              <TabsTrigger value="sbom" data-testid="tab-sbom"><Package className="w-4 h-4 mr-1" /> SBOM</TabsTrigger>
              <TabsTrigger value="controls" data-testid="tab-controls"><ListChecks className="w-4 h-4 mr-1" /> NIST CSF 2.0</TabsTrigger>
              <TabsTrigger value="patches" data-testid="tab-patches"><ArrowUpRight className="w-4 h-4 mr-1" /> Patch plans</TabsTrigger>
            </TabsList>
            <TabsContent value="threats"><ThreatsTab pid={pid} /></TabsContent>
            <TabsContent value="sbom"><SBOMTab pid={pid} /></TabsContent>
            <TabsContent value="controls"><ControlsTab pid={pid} /></TabsContent>
            <TabsContent value="patches"><PatchPlansTab pid={pid} /></TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}

function ThreatsTab({ pid }) {
  const [threats, setThreats] = useState([]);
  const [open, setOpen] = useState(false);
  const [promoteFor, setPromoteFor] = useState(null);
  const [pForm, setPForm] = useState({
    hazard: "", hazardous_situation: "", harm: "",
    severity: 3, probability: 3,
    risk_control_type: "protective_measure", risk_control_description: "",
    residual_severity: 2, residual_probability: 2, benefit_risk_justification: "",
  });
  const [form, setForm] = useState({
    asset: "", stride_category: "Spoofing", threat_description: "",
    attack_vector: "", likelihood: 3, impact: 3, patient_safety_impact: "none",
    existing_mitigations: "", planned_mitigations: "", status: "open",
  });

  const load = useCallback(() => {
    api.get("/cyber/threats", { params: { project_id: pid } })
      .then(({ data }) => setThreats(data))
      .catch((e) => logger.warn("Threats load:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/cyber/threats", { project_id: pid, ...form });
      toast.success("Threat logged.");
      setOpen(false);
      setForm({ asset: "", stride_category: "Spoofing", threat_description: "", attack_vector: "",
                likelihood: 3, impact: 3, patient_safety_impact: "none",
                existing_mitigations: "", planned_mitigations: "", status: "open" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const openPromote = (t) => {
    setPromoteFor(t);
    setPForm({
      hazard: `Cyber threat (${t.stride_category}) on ${t.asset}`,
      hazardous_situation: t.threat_description,
      harm: "Patient safety / data confidentiality / device availability impact — refine.",
      severity: Math.min(5, Math.max(1, t.impact)),
      probability: Math.min(5, Math.max(1, t.likelihood)),
      risk_control_type: "protective_measure",
      risk_control_description: t.planned_mitigations || t.existing_mitigations || "",
      residual_severity: Math.max(1, t.impact - 1),
      residual_probability: Math.max(1, t.likelihood - 1),
      benefit_risk_justification: "",
    });
  };

  const submitPromote = async () => {
    try {
      const { data } = await api.post(`/cyber/threats/${promoteFor.threat_id}/promote-to-risk`, pForm);
      toast.success(`Promoted to risk ${data.risk_id}. Trace on both records.`);
      setPromoteFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promotion failed."); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">
          {threats.length} threat{threats.length === 1 ? "" : "s"} logged · sorted by risk score
        </div>
        <Button data-testid="new-threat-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> Log threat</Button>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Asset</th>
              <th className="text-left px-3 py-2">STRIDE</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-center px-3 py-2">L</th>
              <th className="text-center px-3 py-2">I</th>
              <th className="text-center px-3 py-2">Risk</th>
              <th className="text-center px-3 py-2">Safety</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 w-32"></th>
            </tr>
          </thead>
          <tbody data-testid="threats-tbody">
            {threats.length === 0 && (
              <tr><td colSpan="9" className="text-center px-3 py-6 text-slate-400">No threats yet. Click <b>Log threat</b> to start.</td></tr>
            )}
            {threats.map((t) => (
              <tr key={t.threat_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{t.asset}</td>
                <td className="px-3 py-2"><Badge variant="secondary">{t.stride_category}</Badge></td>
                <td className="px-3 py-2 text-slate-600 max-w-md truncate">{t.threat_description}</td>
                <td className="px-3 py-2 text-center">{t.likelihood}</td>
                <td className="px-3 py-2 text-center">{t.impact}</td>
                <td className="px-3 py-2 text-center font-bold" style={{ color: t.risk_score >= 15 ? "#dc2626" : t.risk_score >= 8 ? "#d97706" : "#059669" }}>
                  {t.risk_score}
                </td>
                <td className="px-3 py-2 text-center text-xs">{t.patient_safety_impact}</td>
                <td className="px-3 py-2 text-center text-xs">{t.status.replace("_", " ")}</td>
                <td className="px-3 py-2 text-right">
                  {t.promoted_risk_id ? (
                    <span className="text-xs text-emerald-700 font-mono" title={`Linked risk ${t.promoted_risk_id}`}>→ {t.promoted_risk_id.slice(0, 12)}</span>
                  ) : (
                    <Button size="sm" variant="outline" data-testid={`promote-${t.threat_id}`} onClick={() => openPromote(t)}>
                      <ArrowUpRight className="w-3 h-3 mr-1" /> Promote
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log a new threat</DialogTitle>
            <DialogDescription>STRIDE-based threat model entry. Likelihood × impact → risk score.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Asset (component, data, interface…)</Label>
              <Input data-testid="threat-asset" value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })} />
            </div>
            <div>
              <Label>STRIDE category</Label>
              <Select value={form.stride_category} onValueChange={(v) => setForm({ ...form, stride_category: v })}>
                <SelectTrigger data-testid="threat-stride"><SelectValue /></SelectTrigger>
                <SelectContent>{STRIDE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Patient safety impact</Label>
              <Select value={form.patient_safety_impact} onValueChange={(v) => setForm({ ...form, patient_safety_impact: v })}>
                <SelectTrigger data-testid="threat-safety"><SelectValue /></SelectTrigger>
                <SelectContent>{SAFETY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Threat description</Label>
              <Textarea data-testid="threat-desc" rows={3} value={form.threat_description} onChange={(e) => setForm({ ...form, threat_description: e.target.value })} />
            </div>
            <div>
              <Label>Likelihood (1–5)</Label>
              <Input data-testid="threat-likelihood" type="number" min={1} max={5} value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: parseInt(e.target.value) || 1 })} />
            </div>
            <div>
              <Label>Impact (1–5)</Label>
              <Input data-testid="threat-impact" type="number" min={1} max={5} value={form.impact} onChange={(e) => setForm({ ...form, impact: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="col-span-2">
              <Label>Existing mitigations (optional)</Label>
              <Textarea data-testid="threat-existing" rows={2} value={form.existing_mitigations} onChange={(e) => setForm({ ...form, existing_mitigations: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Planned mitigations (optional)</Label>
              <Textarea data-testid="threat-planned" rows={2} value={form.planned_mitigations} onChange={(e) => setForm({ ...form, planned_mitigations: e.target.value })} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="threat-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="threat-submit" onClick={submit} disabled={!form.asset || form.threat_description.length < 10}>Save threat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!promoteFor} onOpenChange={(o) => !o && setPromoteFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Promote threat to ISO 14971 risk</DialogTitle>
            <DialogDescription>
              Seeds a formal risk register entry with hazard / hazardous-situation / harm pre-filled from this threat.
              Both records will store a bidirectional cross-reference. ISO 14971 §8 validation applies.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Hazard</Label>
              <Input data-testid="promote-hazard" value={pForm.hazard}
                     onChange={(e) => setPForm({ ...pForm, hazard: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Hazardous situation</Label>
              <Textarea data-testid="promote-situation" rows={2} value={pForm.hazardous_situation}
                        onChange={(e) => setPForm({ ...pForm, hazardous_situation: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Harm</Label>
              <Input data-testid="promote-harm" value={pForm.harm}
                     onChange={(e) => setPForm({ ...pForm, harm: e.target.value })} />
            </div>
            <div>
              <Label>Severity (1–5)</Label>
              <Input data-testid="promote-severity" type="number" min={1} max={5}
                     value={pForm.severity}
                     onChange={(e) => setPForm({ ...pForm, severity: parseInt(e.target.value) || 1 })} />
            </div>
            <div>
              <Label>Probability (1–5)</Label>
              <Input data-testid="promote-probability" type="number" min={1} max={5}
                     value={pForm.probability}
                     onChange={(e) => setPForm({ ...pForm, probability: parseInt(e.target.value) || 1 })} />
            </div>
            <div>
              <Label>Risk control type (ISO 14971 §7.1)</Label>
              <Select value={pForm.risk_control_type} onValueChange={(v) => setPForm({ ...pForm, risk_control_type: v })}>
                <SelectTrigger data-testid="promote-control-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherently_safe_design">Inherently safe design</SelectItem>
                  <SelectItem value="protective_measure">Protective measure</SelectItem>
                  <SelectItem value="information_for_safety">Information for safety</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Residual severity</Label>
              <Input data-testid="promote-res-sev" type="number" min={1} max={5}
                     value={pForm.residual_severity}
                     onChange={(e) => setPForm({ ...pForm, residual_severity: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="col-span-2">
              <Label>Risk control description</Label>
              <Textarea data-testid="promote-control-desc" rows={2} value={pForm.risk_control_description}
                        onChange={(e) => setPForm({ ...pForm, risk_control_description: e.target.value })} />
            </div>
            <div>
              <Label>Residual probability</Label>
              <Input data-testid="promote-res-prob" type="number" min={1} max={5}
                     value={pForm.residual_probability}
                     onChange={(e) => setPForm({ ...pForm, residual_probability: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="col-span-2">
              <Label>Benefit-risk justification <span className="text-slate-400 text-xs">(required if residual RPN ≥ 6)</span></Label>
              <Textarea data-testid="promote-benefit" rows={2} value={pForm.benefit_risk_justification}
                        onChange={(e) => setPForm({ ...pForm, benefit_risk_justification: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPromoteFor(null)}>Cancel</Button>
            <Button data-testid="promote-submit" onClick={submitPromote}
                    disabled={pForm.hazard.length < 5 || pForm.hazardous_situation.length < 5 || pForm.harm.length < 3}>
              Create linked risk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SBOMTab({ pid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ component_name: "", version: "", supplier: "", license: "", purl: "", is_open_source: true });

  const load = useCallback(() => {
    api.get("/cyber/sbom", { params: { project_id: pid } })
      .then(({ data }) => setItems(data))
      .catch((e) => logger.warn("SBOM load:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/cyber/sbom", { project_id: pid, ...form });
      toast.success("Component added to SBOM.");
      setOpen(false);
      setForm({ component_name: "", version: "", supplier: "", license: "", purl: "", is_open_source: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this SBOM component?")) return;
    try { await api.delete(`/cyber/sbom/${id}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">{items.length} component{items.length === 1 ? "" : "s"}</div>
        <Button data-testid="new-sbom-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> Add component</Button>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Component</th>
              <th className="text-left px-3 py-2">Version</th>
              <th className="text-left px-3 py-2">Supplier</th>
              <th className="text-left px-3 py-2">License</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="sbom-tbody">
            {items.length === 0 && (
              <tr><td colSpan="6" className="text-center px-3 py-6 text-slate-400">No components tracked yet.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.sbom_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{c.component_name}</td>
                <td className="px-3 py-2 font-mono">{c.version}</td>
                <td className="px-3 py-2 text-slate-600">{c.supplier || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{c.license || "—"}</td>
                <td className="px-3 py-2 text-xs"><Badge variant="secondary">{c.is_open_source ? "OSS" : "Commercial"}</Badge></td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => remove(c.sbom_id)}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add SBOM component</DialogTitle>
            <DialogDescription>Software Bill of Materials — supports FDA 2023 and EU CRA vulnerability handling.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Component name</Label><Input data-testid="sbom-name" value={form.component_name} onChange={(e) => setForm({ ...form, component_name: e.target.value })} /></div>
            <div><Label>Version</Label><Input data-testid="sbom-version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
            <div><Label>Supplier</Label><Input data-testid="sbom-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
            <div><Label>License</Label><Input data-testid="sbom-license" value={form.license} onChange={(e) => setForm({ ...form, license: e.target.value })} /></div>
            <div className="col-span-2"><Label>Package URL (purl)</Label><Input data-testid="sbom-purl" value={form.purl} onChange={(e) => setForm({ ...form, purl: e.target.value })} placeholder="pkg:npm/react@19.0.0" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="sbom-submit" onClick={submit} disabled={!form.component_name || !form.version}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ControlsTab({ pid }) {
  const [data, setData] = useState({ controls: [], counts: {}, total: 0 });

  const load = useCallback(() => {
    api.get(`/cyber/controls/${pid}`)
      .then(({ data }) => setData(data))
      .catch((e) => logger.warn("Controls load:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (control_id, status) => {
    try {
      await api.post("/cyber/controls", { project_id: pid, control_id, status });
      toast.success("Updated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const groups = data.controls.reduce((acc, c) => { (acc[c.function] = acc[c.function] || []).push(c); return acc; }, {});
  const pct = data.total ? Math.round(100 * ((data.counts.evidence_documented || 0) + (data.counts.not_applicable || 0)) / data.total) : 0;

  return (
    <div className="mt-6 space-y-4">
      <div className="vq-card p-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <div className="font-bold text-2xl text-emerald-700" data-testid="controls-readiness-pct">{pct}%</div>
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900">NIST CSF 2.0 evidence completeness</div>
          <div className="text-xs text-slate-500 mt-1">
            {data.counts.evidence_documented || 0} documented · {data.counts.in_progress || 0} in progress · {data.counts.not_started || 0} not started · {data.counts.not_applicable || 0} N/A
          </div>
        </div>
      </div>

      {Object.entries(groups).map(([fn, ctrls]) => (
        <div key={fn} className="vq-card">
          <div className="p-3 border-b bg-slate-50 font-display font-bold text-sm text-slate-900">{fn}</div>
          <table className="w-full text-sm">
            <tbody data-testid={`controls-${fn.toLowerCase().replace(/\s/g, "-")}`}>
              {ctrls.map((c) => (
                <tr key={c.control_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs w-24">{c.control_id}</td>
                  <td className="px-3 py-2 text-slate-700">{c.description}</td>
                  <td className="px-3 py-2 w-52">
                    <Select value={c.status} onValueChange={(v) => updateStatus(c.control_id, v)}>
                      <SelectTrigger data-testid={`ctrl-status-${c.control_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CONTROL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="vq-card p-4 border-l-4 border-l-amber-500 bg-amber-50 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900">
          These controls are a starting scaffold. NIST CSF 2.0 is not a certification; a qualified cybersecurity
          reviewer must complete the full assessment aligned to FDA 2023, IEC 81001-5-1, and (if applicable) the
          EU Cyber Resilience Act.
        </div>
      </div>
    </div>
  );
}

const PATCH_STATUSES = ["planned", "in_progress", "deployed", "verified", "wont_fix"];
const PATCH_SEVERITY = ["low", "medium", "high", "critical"];

function PatchPlansTab({ pid }) {
  const [plans, setPlans] = useState([]);
  const [threats, setThreats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", vuln_reference: "", severity: "medium",
    target_release_date: "", mitigation_plan: "",
    linked_threat_id: "", status: "planned", responsible_owner: "",
    affected_components: "",
  });

  const load = useCallback(() => {
    api.get("/cyber/patch-plans", { params: { project_id: pid } })
      .then(({ data }) => setPlans(data))
      .catch((e) => logger.warn("Patch plans:", e?.response?.status));
    api.get("/cyber/threats", { params: { project_id: pid } })
      .then(({ data }) => setThreats(data))
      .catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        project_id: pid,
        affected_components: form.affected_components.split(",").map((s) => s.trim()).filter(Boolean),
        linked_threat_id: form.linked_threat_id || null,
        target_release_date: form.target_release_date || null,
      };
      await api.post("/cyber/patch-plans", payload);
      toast.success("Patch plan created.");
      setOpen(false);
      setForm({ title: "", vuln_reference: "", severity: "medium",
                target_release_date: "", mitigation_plan: "",
                linked_threat_id: "", status: "planned", responsible_owner: "",
                affected_components: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const updateStatus = async (plan_id, status) => {
    try {
      await api.patch(`/cyber/patch-plans/${plan_id}`, { status });
      toast.success("Updated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">{plans.length} patch plan{plans.length === 1 ? "" : "s"}</div>
        <Button data-testid="new-patch-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> New patch plan</Button>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">CVE/Ref</th>
              <th className="text-center px-3 py-2">Severity</th>
              <th className="text-left px-3 py-2">Target date</th>
              <th className="text-left px-3 py-2 w-52">Status</th>
            </tr>
          </thead>
          <tbody data-testid="patches-tbody">
            {plans.length === 0 && (
              <tr><td colSpan="5" className="text-center px-3 py-6 text-slate-400">No patch plans yet.</td></tr>
            )}
            {plans.map((p) => (
              <tr key={p.plan_id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium">{p.title}</div>
                  {p.linked_threat_id && (
                    <div className="text-xs text-slate-500 font-mono">threat: {p.linked_threat_id}</div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{p.vuln_reference || "—"}</td>
                <td className="px-3 py-2 text-center">
                  <Badge variant={p.severity === "critical" || p.severity === "high" ? "destructive" : "secondary"}>
                    {p.severity}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs">{p.target_release_date || "—"}</td>
                <td className="px-3 py-2">
                  <Select value={p.status} onValueChange={(v) => updateStatus(p.plan_id, v)}>
                    <SelectTrigger data-testid={`patch-status-${p.plan_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PATCH_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New patch plan</DialogTitle>
            <DialogDescription>FDA 2023 post-market cybersecurity + EU CRA vulnerability handling.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Title</Label>
              <Input data-testid="patch-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Vulnerability reference (CVE / advisory)</Label>
              <Input data-testid="patch-vuln" value={form.vuln_reference} onChange={(e) => setForm({ ...form, vuln_reference: e.target.value })} placeholder="CVE-2024-XXXX" />
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="patch-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{PATCH_SEVERITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target release date</Label>
              <Input data-testid="patch-date" type="date" value={form.target_release_date} onChange={(e) => setForm({ ...form, target_release_date: e.target.value })} />
            </div>
            <div>
              <Label>Linked threat (optional)</Label>
              <Select value={form.linked_threat_id || "__none"} onValueChange={(v) => setForm({ ...form, linked_threat_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="patch-link"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {threats.map((t) => (
                    <SelectItem key={t.threat_id} value={t.threat_id}>
                      {t.asset} · {t.stride_category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Affected components (comma-separated)</Label>
              <Input data-testid="patch-affected" value={form.affected_components} onChange={(e) => setForm({ ...form, affected_components: e.target.value })} placeholder="nginx, openssl, curl" />
            </div>
            <div className="col-span-2">
              <Label>Mitigation plan</Label>
              <Textarea data-testid="patch-plan" rows={4} value={form.mitigation_plan} onChange={(e) => setForm({ ...form, mitigation_plan: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Responsible owner (optional)</Label>
              <Input data-testid="patch-owner" value={form.responsible_owner} onChange={(e) => setForm({ ...form, responsible_owner: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="patch-submit" onClick={submit}
                    disabled={form.title.length < 3 || form.mitigation_plan.length < 10}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

